import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaArrowDown } from "react-icons/fa";
import backgroundVideo from "../../assets/videos/bg-portfolio.webm";
import "../../styles/hero.css";

const Hero = () => {
  const heroRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const lastMouseTime = useRef(Date.now());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isLoaded) return;
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMousePos({ x, y });
        lastMouseTime.current = Date.now();
      }
    };

    const hero = heroRef.current;
    hero.addEventListener("mousemove", handleMouseMove);
    return () => hero.removeEventListener("mousemove", handleMouseMove);
  }, [isLoaded]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const videoTransform = () => {
    if (!heroRef.current) return "translate(0px, 0px)";
    const rect = heroRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const offsetX = (mousePos.x - centerX) * 0.01;
    const offsetY = (mousePos.y - centerY) * 0.01;
    return `translate(${offsetX}px, ${offsetY}px)`;
  };

  return (
    <motion.section
      ref={heroRef}
      id="hero-section"
      className="hero-section scroll-section"
      initial={{ opacity: 0 }}
      animate={{ scale: [2, 1.5, 1], opacity: 1 }}
      transition={{
        scale: { duration: 5, times: [0, 0.7, 1], ease: "easeOut" },
        opacity: { duration: 1, ease: "easeOut" },
      }}
      style={{ overflow: "hidden", position: "relative" }}
    >
      <motion.div
        className="hero-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, delay: 4 }}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "80vw" : "600px",
          height: isMobile ? "80vw" : "600px",
          borderRadius: "50%",
          display: "flex",
          overflow: "visible",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "invert(1)",
          background: "rgba(0, 0, 0, 0.2)",
          zIndex: 3,
        }}
      >
        <motion.div
          className="hero-text"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          display={isMobile ? "none" : "block"}
        >
          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.7 }}
            style={{ zIndex: 2 }}
          >
            <span className="title-top">Connecting</span>
            <span className="title-highlight">
              <span className="boxed-title">Design</span>+<span className="boxed-title">Code</span>
            </span>{" "}
            <span className="title-bottom">to bring ideas to life</span>
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.9 }}
          >
            That <span className="subtitle-highlight">Converts, Inspires</span> and{" "}
            <span className="subtitle-highlight">Connects People </span>
            to Deliver complete creative solutions
          </motion.p>
        </motion.div>
      </motion.div>

      <motion.div
        className="arrow-down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        whileHover={{ scale: 1.5 }}
        transition={{
          opacity: { delay: 2.1, duration: 0.5 },
          y: { repeat: Infinity, duration: 1.5, ease: "easeInOut" },
          scale: { duration: 0 },
        }}
        onClick={() => {
          const nextSection = document.querySelector(".scroll-section:nth-of-type(2)");
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        <FaArrowDown />
      </motion.div>

      {/* Background Video */}
      <video
        src={backgroundVideo}
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "110%",
          height: "110%",
          objectFit: "cover",
          overflow: "visible",
          zIndex: 1,
          transform: videoTransform(),
        }}
      />
    </motion.section>
  );
};

export default Hero;
